import { QuillDeltaToHtmlConverter, GroupType } from 'quill-delta-to-html';

type NodeType = 'div' | 'ol' | 'ul' | 'li' | 'h1' | 'h2' | 'h3';
export interface INode {
  text?: string; // text content for leaf blocks like li/div/h*
  type: NodeType;
  ref?: Array<string | number>;
  children?: INode[];
}

type Op = { insert: string | object; attributes?: Record<string, any> };
type Delta = { ops: Op[] };

export function nodesToDelta(nodes: INode[]): Delta {
  const ops: Op[] = [];
  walk(nodes, ops, 0, null);
  return { ops };
}

/** Walk the tree and push Quill ops */
type ListCtx = 'bullet' | 'ordered' | null;

function normalizeRef(
  ref?: readonly (string | number)[] | null,
): string | undefined {
  if (!ref || ref.length === 0) return undefined;
  return ref.map(String).join(',');
}

function walk(
  nodes: INode[] | undefined,
  ops: Op[],
  indent: number,
  listContext: ListCtx,
): void {
  if (!nodes?.length) return;

  for (const n of nodes) {
    const ref = normalizeRef(n.ref);

    switch (n.type) {
      case 'div': {
        if (n.children?.length) {
          walk(n.children, ops, indent, null);
          ensureTrailingNewline(ops);
          setAttrsOnLastNewline(ops, { ref });
        } else {
          insertTextWithBlock(ops, (n.text ?? '').replace(/\n/g, ' '), { ref });
        }
        break;
      }

      case 'h1':
      case 'h2':
      case 'h3': {
        const level = n.type === 'h1' ? 1 : n.type === 'h2' ? 2 : 3;
        const text = (n.text ?? '').replace(/\n/g, ' ');
        if (text) ops.push({ insert: text });
        ops.push({ insert: '\n', attributes: clean({ header: level, ref }) });

        // ⬇ NEW: handle children after the heading
        if (n.children?.length) {
          const [lists, others] = partition(
            n.children,
            (c) => c.type === 'li' || c.type === 'ul' || c.type === 'ol',
          );

          // If heading has direct <li> children, treat as an implicit <ul>
          const directLis = lists.filter((c) => c.type === 'li');
          if (directLis.length) {
            walk(directLis as INode[], ops, 0, 'bullet');
          }

          // If there are explicit <ul>/<ol> children, let walk handle them
          const explicitLists = lists.filter(
            (c) => c.type === 'ul' || c.type === 'ol',
          );
          if (explicitLists.length) {
            walk(explicitLists as INode[], ops, 0, null);
          }

          // Any non-list children render as normal flow
          if (others.length) {
            walk(others, ops, 0, null);
          }
        }
        break;
      }

      case 'ul':
      case 'ol': {
        const newCtx: ListCtx = n.type === 'ul' ? 'bullet' : 'ordered';
        const nextIndent = listContext ? indent + 1 : indent;
        walk(n.children, ops, nextIndent, newCtx);
        break;
      }

      case 'li': {
        const lineText = (n.text ?? '').replace(/\n/g, ' ');
        if (lineText) ops.push({ insert: lineText });
        ops.push({
          insert: '\n',
          attributes: clean({
            list: listContext ?? 'bullet',
            indent: indent > 0 ? indent : undefined,
            ref,
          }),
        });

        // Nested under this <li>
        if (n.children?.length) {
          const [lists, others] = partition(
            n.children,
            (c) => c.type === 'ul' || c.type === 'ol',
          );
          if (others.length) walk(others, ops, indent, null);
          for (const l of lists) {
            const lCtx: ListCtx = l.type === 'ul' ? 'bullet' : 'ordered';
            walk(l.children, ops, indent + 1, lCtx);
          }
        }
        break;
      }

      default: {
        insertTextWithBlock(ops, (n.text ?? '').replace(/\n/g, ' '), { ref });
      }
    }
  }
}

/** Insert plain text followed by a paragraph newline carrying attributes */
function insertTextWithBlock(
  ops: Op[],
  text: string,
  attrs?: Record<string, any>,
) {
  const safe = (text ?? '').replace(/\n/g, ' ');
  if (safe) ops.push({ insert: safe });
  ops.push({ insert: '\n', attributes: clean(attrs ?? {}) });
}

/** Ensure the last op is a newline (adds a blank paragraph if needed) */
function ensureTrailingNewline(ops: Op[]) {
  const last = ops[ops.length - 1];
  if (
    !last ||
    !(typeof last.insert === 'string' && last.insert.endsWith('\n'))
  ) {
    ops.push({ insert: '\n' });
  }
}

/** Attach/merge attributes onto the last newline op, if present */
function setAttrsOnLastNewline(ops: Op[], add: Record<string, any>) {
  for (let i = ops.length - 1; i >= 0; i--) {
    const op = ops[i];
    if (typeof op.insert === 'string' && op.insert === '\n') {
      op.attributes = clean({ ...(op.attributes || {}), ...add });
      return;
    }
    if (typeof op.insert === 'string' && op.insert.includes('\n')) {
      // Split not handled here—keep simple; typical flow already inserts newline separately
      break;
    }
  }
}

/** Remove undefined/null keys to keep the delta tidy */
function clean<T extends Record<string, any>>(obj: T): T | undefined {
  const out: Record<string, any> = {};
  let any = false;
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v !== undefined && v !== null) {
      out[k] = v;
      any = true;
    }
  }
  return any ? (out as T) : undefined;
}

function partition<T>(arr: T[] = [], pred: (t: T) => boolean): [T[], T[]] {
  const a: T[] = [],
    b: T[] = [];
  for (const x of arr) (pred(x) ? a : b).push(x);
  return [a, b];
}
/* 


  convertDeltaToHtmlWithRefs 


 */
type ISizeMap = Record<string, string>;

export function convertDeltaToHtmlWithRefs(ops: any[], sizeMap?: ISizeMap) {
  // 1) Compact block list: one entry per newline (in visual order)
  const blocks: { tag: string; ref?: string }[] = [];
  for (const op of ops) {
    if (op?.insert !== '\n') continue;
    const a = op.attributes || {};
    const tag = a.list
      ? 'li'
      : a.header
        ? `h${Math.min(Math.max(+a.header, 1), 6)}`
        : 'p';
    blocks.push({ tag, ref: a.ref?.toString() });
  }

  // 2) Convert Delta to HTML (one block per line)
  const conv = new QuillDeltaToHtmlConverter(ops, {
    inlineStyles: sizeMap ? { size: sizeMap } : undefined,
    multiLineParagraph: false,
  });
  const html = conv.convert();

  // 3) Inject data-ref on matching nodes in order
  const doc = new DOMParser().parseFromString(
    `<div id="r">${html}</div>`,
    'text/html',
  );
  const root = doc.getElementById('r')!;
  const nodes = root.querySelectorAll('h1,h2,h3,h4,h5,h6,p,li');

  let i = 0;
  nodes.forEach((node) => {
    if (i >= blocks.length) return;
    const want = blocks[i];
    // keep it simple: advance until tag matches (handles empty headers/lines)
    while (
      want &&
      node.tagName.toLowerCase() !== want.tag &&
      i < blocks.length - 1
    ) {
      i++;
    }
    const meta = blocks[i];
    if (meta && node.tagName.toLowerCase() === meta.tag && meta.ref) {
      node.setAttribute('data-ref', meta.ref);
    }
    i++;
  });

  return root.innerHTML;
}
